import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectReportsData,
  selectAuditLogsItems,
  selectAuditLogsLoading,
  selectAuditLogsError,
} from "../../Redux/Slices/reportsSlice";
import {
  createMonthLock,
  fetchAuditLogs,
  fetchMonthLocks,
  unlockMonthLock,
} from "../../Redux/Thunks/reportsThunk";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import { getListFromApiResponse } from "../utils/apiListMappers";
import {
  displayMonthKeyToIsoMonth,
  generateLockMonthWindowOptions,
  getCurrentMonthDisplayKey,
  sortMonthYearDropdownOptions,
} from "../../utils/monthLockApi";
import Typography from "../../Utility/Typography";
import { LockMonthModal, UnlockMonthModal } from "./MonthLockModals";
import MonthLockingSection from "./MonthLockingSection";
import AuditLogPanel from "./AuditLogPanel";
import AddButton from "../../Utility/AddButton/AddButton";
import { usePermissions } from "../../hooks/usePermissions";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import { getApiErrorMessage, isSilentRequestAbortError } from "../../utils/apiErrorMessage";
import { downloadAuditLogExport } from "../../utils/downloadAuditLogExport";

function resolveClientId(clientLabel, clientsList) {
  if (!clientLabel || clientLabel === "Global") return undefined;
  const row = clientsList.find((c) => (c.clientName ?? c.name ?? "").trim() === clientLabel.trim());
  return row?.id ?? row?.clientId ?? undefined;
}

const Reports = () => {
  const dispatch = useDispatch();
  const data = useSelector(selectReportsData);
  const auditLogs = useSelector(selectAuditLogsItems);
  const auditLogsLoading = useSelector(selectAuditLogsLoading);
  const auditLogsError = useSelector(selectAuditLogsError);
  const auditTotal = useSelector((state) => state.reports.auditLogs.total);
  const auditPage = useSelector((state) => state.reports.auditLogs.page);
  const auditLimit = useSelector((state) => state.reports.auditLogs.limit);
  const clientsData = useSelector(selectClientsData);

  const [activeMonth, setActiveMonth] = useState(null);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  const [lockMonthValue, setLockMonthValue] = useState("");
  const [lockClientValue, setLockClientValue] = useState("Global");
  const [lockUseAutoLock, setLockUseAutoLock] = useState(false);
  const [lockSubmitting, setLockSubmitting] = useState(false);
  const [unlockSubmitting, setUnlockSubmitting] = useState(false);
  const [auditExporting, setAuditExporting] = useState(false);
  const [auditExportError, setAuditExportError] = useState("");
  const [lockError, setLockError] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const hasFetchedRef = useRef(false);
  const lastAuditParamsRef = useRef({ page: 1, limit: 20 });
  const lastMonthLockParamsRef = useRef({ page: 1, limit: 20 });
  /** Stable lock id while unlock modal is open (avoids silent no-op if `activeMonth` is cleared). */
  const unlockLockIdRef = useRef("");
  const { can } = usePermissions();
  const canLockMonth = can(PERMISSION_KEYS.MONTH_LOCK_MANAGE);
  const canUnlockMonth = can(PERMISSION_KEYS.MONTH_LOCK_UNLOCK);
  const canExportAuditLogs = can(PERMISSION_KEYS.REPORTS_AUDIT_EXPORT);

  const requestMonthLocks = useCallback(
    (filters = {}) => {
      const q = { page: 1, limit: 20 };
      if (filters.month) q.month = filters.month;
      if (filters.client_id) q.client_id = filters.client_id;
      lastMonthLockParamsRef.current = q;
      dispatch(fetchMonthLocks(q));
    },
    [dispatch]
  );

  const requestAuditLogs = useCallback(
    (patch = {}) => {
      const next = { ...lastAuditParamsRef.current, ...patch };
      lastAuditParamsRef.current = next;
      dispatch(fetchAuditLogs(next));
    },
    [dispatch]
  );

  const clientsList = getListFromApiResponse(clientsData);
  const clientsListLen = clientsList.length;

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    requestMonthLocks({});
    requestAuditLogs({ page: 1, limit: 5 });
  }, [requestAuditLogs, requestMonthLocks]);

  useEffect(() => {
    if (clientsListLen === 0) dispatch(fetchClientsData({ getAll: true }));
  }, [dispatch, clientsListLen]);

  const monthLocks = data?.monthLocks || [];
  const monthLocksLoading = Boolean(data?.monthLocksLoading);

  /** Lock Month modal: previous 12 months + current + next 5 months from today. */
  const lockFormMonthOptions = useMemo(() => generateLockMonthWindowOptions(12, 5), []);

  const lockMonthOptions = useMemo(() => {
    const map = new Map();
    monthLocks.forEach((m) => {
      const v = `${m.month}|${m.year}`;
      if (!map.has(v)) map.set(v, `${m.month} ${m.year}`);
    });
    lockFormMonthOptions.forEach((o) => {
      if (!map.has(o.value)) map.set(o.value, o.label);
    });
    return sortMonthYearDropdownOptions(
      Array.from(map.entries()).map(([value, label]) => ({ value, label }))
    );
  }, [monthLocks, lockFormMonthOptions]);

  const lockClientOptions = useMemo(() => {
    const names = clientsList.map((c) => c.clientName ?? c.name ?? "").filter(Boolean);
    const sorted = [...new Set(names)].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
    return ["Global", ...sorted];
  }, [clientsList]);

  const applyLockSelectionFromMonth = useCallback((month) => {
    if (!month) return;
    setLockMonthValue(`${month.month}|${month.year}`);
    setLockClientValue(month.client === "Global" ? "Global" : month.client || "Global");
  }, []);

  const handleOpenLockModal = (month) => {
    setActiveMonth(month);
    setLockUseAutoLock(false);
    if (month) applyLockSelectionFromMonth(month);
    setLockError("");
    setLockModalOpen(true);
  };

  const handleOpenUnlockModal = (month) => {
    setActiveMonth(month);
    unlockLockIdRef.current = month?.id != null ? String(month.id).trim() : "";
    setUnlockReason("");
    setUnlockError("");
    setUnlockModalOpen(true);
  };

  const handleConfirmLock = async () => {
    if (!lockMonthValue || !lockClientValue) {
      setLockError("Select month and client.");
      return;
    }
    const monthIso = displayMonthKeyToIsoMonth(lockMonthValue);
    if (!monthIso) {
      setLockError("Invalid month selection.");
      return;
    }
    const clientLabel = lockClientValue;
    const clientId = resolveClientId(clientLabel, clientsList);

    const lockPayload = {
      month: monthIso,
      useAutoLock: lockUseAutoLock,
      clientLabel,
    };
    if (clientId) {
      lockPayload.clientId = clientId;
    }

    setLockSubmitting(true);
    setLockError("");
    try {
      await dispatch(createMonthLock(lockPayload)).unwrap();
      requestMonthLocks(lastMonthLockParamsRef.current);
      requestAuditLogs({});
      setLockModalOpen(false);
    } catch (err) {
      setLockError(getApiErrorMessage(err, "Failed to create month lock"));
    } finally {
      setLockSubmitting(false);
    }
  };

  const handleConfirmUnlock = async () => {
    const lockId = String(unlockLockIdRef.current || activeMonth?.id || "").trim();
    if (!lockId) return;
    const trimmed = String(unlockReason ?? "").trim();
    if (!trimmed) {
      setUnlockError("Reason is required.");
      return;
    }

    setUnlockSubmitting(true);
    setUnlockError("");
    try {
      await dispatch(
        unlockMonthLock({
          lockId,
          reason: trimmed,
        })
      ).unwrap();
      requestMonthLocks(lastMonthLockParamsRef.current);
      requestAuditLogs({});
      setUnlockModalOpen(false);
      setUnlockReason("");
      unlockLockIdRef.current = "";
    } catch (err) {
      setUnlockError(getApiErrorMessage(err, "Failed to unlock month"));
    } finally {
      setUnlockSubmitting(false);
    }
  };

  const monthLabel = activeMonth && `${activeMonth.month} ${activeMonth.year}`;

  return (
    <>
      <div className="px-4 sm:px-6 lg:px-8 py-4 space-y-4 h-auto flex flex-col">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <Typography variant="h4" className="text-text-primary">
              Month Locking &amp; Audit
            </Typography>
            <Typography variant="text2" className="text-text-secondary mt-1">
              Manage locked billing periods and review change history.
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            {can(PERMISSION_KEYS.MONTH_LOCK_MANAGE) ? (
              <AddButton
                onClick={() => {
                  const firstUnlocked = monthLocks.find((m) => m.status === "Unlocked");
                  if (firstUnlocked) {
                    handleOpenLockModal(firstUnlocked);
                  } else if (lockMonthOptions.length > 0) {
                    setActiveMonth(null);
                    const preferred = getCurrentMonthDisplayKey();
                    const defaultMonth = lockMonthOptions.some((o) => o.value === preferred)
                      ? preferred
                      : lockMonthOptions[0].value;
                    setLockMonthValue(defaultMonth);
                    setLockClientValue("Global");
                    setLockUseAutoLock(false);
                    setLockModalOpen(true);
                  }
                }}
                tooltip="Lock New Month"
              />
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 flex-1 overflow-hidden">
          <MonthLockingSection
            monthLocks={monthLocks}
            loading={monthLocksLoading}
            clients={clientsList}
            canLockMonth={canLockMonth}
            canUnlockMonth={canUnlockMonth}
            onRequestMonthLocks={requestMonthLocks}
            onOpenLockModal={(month) => {
              if (!can(PERMISSION_KEYS.MONTH_LOCK_MANAGE)) {
                setDeniedPermissionKey(PERMISSION_KEYS.MONTH_LOCK_MANAGE);
                return;
              }
              handleOpenLockModal(month);
            }}
            onOpenUnlockModal={(month) => {
              if (!can(PERMISSION_KEYS.MONTH_LOCK_UNLOCK)) {
                setDeniedPermissionKey(PERMISSION_KEYS.MONTH_LOCK_UNLOCK);
                return;
              }
              handleOpenUnlockModal(month);
            }}
          />

          <AuditLogPanel
            auditLogs={auditLogs}
            auditLoading={auditLogsLoading}
            auditError={auditLogsError}
            total={auditTotal}
            page={auditPage}
            limit={auditLimit}
            onFetchAuditLogs={requestAuditLogs}
            canViewAudit={can(PERMISSION_KEYS.REPORTS_LOCK_AUDIT_VIEW)}
            canExportAudit={canExportAuditLogs}
            auditExporting={auditExporting}
            exportError={auditExportError}
            onExportAudit={async (format = "csv") => {
              if (!can(PERMISSION_KEYS.REPORTS_AUDIT_EXPORT)) {
                setDeniedPermissionKey(PERMISSION_KEYS.REPORTS_AUDIT_EXPORT);
                return;
              }
              if (auditExporting) return;
              setAuditExporting(true);
              setAuditExportError("");
              try {
                const { page: _p, limit: _l, ...exportFilters } = lastAuditParamsRef.current;
                await downloadAuditLogExport(exportFilters, format);
              } catch (err) {
                if (isSilentRequestAbortError(err)) return;
                setAuditExportError(getApiErrorMessage(err, "Export failed."));
              } finally {
                setAuditExporting(false);
              }
            }}
          />
        </div>

        {/* Modals */}
      </div>
      <LockMonthModal
        isOpen={lockModalOpen}
        onClose={() => {
          setLockModalOpen(false);
          setLockError("");
        }}
        monthOptions={lockMonthOptions}
        monthValue={lockMonthValue}
        onMonthChange={setLockMonthValue}
        clientOptions={lockClientOptions}
        clientValue={lockClientValue}
        onClientChange={setLockClientValue}
        useAutoLock={lockUseAutoLock}
        onUseAutoLockChange={setLockUseAutoLock}
        onConfirm={handleConfirmLock}
        errorMessage={lockError}
        loading={lockSubmitting}
      />
      <UnlockMonthModal
        isOpen={unlockModalOpen}
        onClose={() => {
          setUnlockModalOpen(false);
          unlockLockIdRef.current = "";
          setUnlockError("");
        }}
        monthLabel={monthLabel}
        client={activeMonth?.client || ""}
        onConfirm={handleConfirmUnlock}
        reason={unlockReason}
        setReason={setUnlockReason}
        errorMessage={unlockError}
        loading={unlockSubmitting}
      />
      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </>
  );
};

export default Reports;
